import React from 'react';
import { createBrowserRouter, RouteObject } from 'react-router-dom';
import RouterAwareAuthProvider from '../components/RouterAwareAuthProvider';
import RootLayout from '../components/layout/RootLayout';
import MainLayout from '../components/layout/MainLayout';
import AuthLayout from '../components/layout/AuthLayout';
import AuthGuard from '../components/guards/AuthGuard';
import AdminGuard from '../components/guards/AdminGuard';
import LoadingScreen from '../components/LoadingScreen';

// Lazy load pages
const Login = React.lazy(() => import('../pages/auth/Login'));
const Register = React.lazy(() => import('../pages/auth/Register'));
const ForgotPasswordPage = React.lazy(() => import('../pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('../pages/auth/ResetPasswordPage'));
const VerifyEmailPage = React.lazy(() => import('../pages/auth/VerifyEmailPage'));
const SupportPage = React.lazy(() => import('../pages/SupportPage'));
const Home = React.lazy(() => import('../pages/Home'));
const SearchPage = React.lazy(() => import('../pages/search/SearchPageModern'));
const CreatePost = React.lazy(() => import('../pages/CreatePost'));
const CreateServiceReview = React.lazy(() => import('../pages/CreateServiceReview'));
const PostPage = React.lazy(() => import('../pages/PostPage'));
const PostEditPage = React.lazy(() => import('../pages/PostEditPage'));
const Profile = React.lazy(() => import('../pages/Profile'));
const LegacyProfileRedirect = React.lazy(() => import('../components/LegacyProfileRedirect'));
const SessionProfilePage = React.lazy(() => import('../pages/SessionProfilePage'));
const Settings = React.lazy(() => import('../pages/Settings'));
const BusinessSettings = React.lazy(() => import('../pages/settings/BusinessSettings'));
const NotificationsPage = React.lazy(() => import('../pages/notifications/NotificationsPage'));
const NotFound = React.lazy(() => import('../pages/NotFound'));
const BusinessSetupWizard = React.lazy(() => import('../components/business/BusinessSetupWizard'));
const Messages = React.lazy(() => import('../pages/Messages'));

// Dev-only debug/demo pages. The inline `process.env.NODE_ENV` checks are
// replaced at build time, so webpack drops these imports (and their chunks)
// entirely from production builds. Their routes are registered further below
// under the same condition.
const DebugAuth = (process.env.NODE_ENV !== 'production'
  ? React.lazy(() => import('../pages/DebugAuth'))
  : () => null) as React.ComponentType;
const QuickLogin = (process.env.NODE_ENV !== 'production'
  ? React.lazy(() => import('../pages/QuickLogin'))
  : () => null) as React.ComponentType;
const TestNoAuth = (process.env.NODE_ENV !== 'production'
  ? React.lazy(() => import('../pages/TestNoAuth'))
  : () => null) as React.ComponentType;
const ResponsiveTest = (process.env.NODE_ENV !== 'production'
  ? React.lazy(() => import('../pages/ResponsiveTest'))
  : () => null) as React.ComponentType;
const RedditClonePage = (process.env.NODE_ENV !== 'production'
  ? React.lazy(() => import('../pages/RedditClonePage'))
  : () => null) as React.ComponentType;
const RedditStyleDemo = (process.env.NODE_ENV !== 'production'
  ? React.lazy(() => import('../pages/RedditStyleDemo'))
  : () => null) as React.ComponentType;
// Category pages
const CategoriesPage = React.lazy(() => import('../pages/CategoriesPage'));
const CategoryPage = React.lazy(() => import('../pages/CategoryPage'));
const CategoryAnalyticsPage = React.lazy(() => import('../pages/CategoryAnalyticsPage'));
const BrandPage = React.lazy(() => import('../pages/BrandPage'));
const BrandsPage = React.lazy(() => import('../pages/BrandsPage'));
const ExplorePage = React.lazy(() => import('../pages/ExplorePage'));
const BrowsePage = React.lazy(() => import('../pages/BrowsePage'));
// TEMPORARILY DISABLED — pricing & subscriptions hidden until community grows
// const PricingPage = React.lazy(() => import('../pages/PricingPage'));
// const SubscriptionSuccess = React.lazy(() => import('../pages/subscription/SubscriptionSuccess'));
const AnalyticsDashboard = React.lazy(() => import('../pages/AnalyticsDashboard'));

// Policy pages
const PrivacyPolicyPage = React.lazy(() => import('../pages/policies/PrivacyPolicyPage'));
const TermsOfServicePage = React.lazy(() => import('../pages/policies/TermsOfServicePage'));
const CookiePolicyPage = React.lazy(() => import('../pages/policies/CookiePolicyPage'));
const DataProtectionPage = React.lazy(() => import('../pages/policies/DataProtectionPage'));
const ServiceAgreementPage = React.lazy(() => import('../pages/policies/ServiceAgreementPage'));
const ServiceStandardsPage = React.lazy(() => import('../pages/policies/ServiceStandardsPage'));
const DisputeResolutionPage = React.lazy(() => import('../pages/policies/DisputeResolutionPage'));

// Everything Else section
const EverythingElseFeed = React.lazy(() => import('../pages/EverythingElseFeed'));
const CreateExperiencePost = React.lazy(() => import('../pages/everything/CreateExperiencePost'));
const EverythingPostDetail = React.lazy(() => import('../pages/everything/EverythingPostDetail'));

// Alias AuthGuard as RequireAuth for the new routing structure
const RequireAuth = AuthGuard;
const AdminLayout = React.lazy(() => import('../components/admin/layout/AdminLayout'));
const AdminDashboard = React.lazy(() => import('../pages/admin/Dashboard'));
const UserManagement = React.lazy(() => import('../pages/admin/UserManagement'));
const Verifications = React.lazy(() => import('../pages/admin/Verifications'));
const Reports = React.lazy(() => import('../pages/admin/moderation/Reports'));
const AdminSettings = React.lazy(() => import('../pages/admin/system/Settings'));
const Metrics = React.lazy(() => import('../pages/admin/system/Metrics'));
const Security = React.lazy(() => import('../pages/admin/system/Security'));
// const AdCampaigns = React.lazy(() => import('../pages/admin/AdCampaigns'));  // TEMPORARILY DISABLED

const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: (
      <React.Suspense fallback={<LoadingScreen />}>
        <NotFound />
      </React.Suspense>
    ),
    children: [
      {
        path: '',
        element: <RouterAwareAuthProvider />,
        children: [
          {
            path: '',
            element: <MainLayout showFooter={false} />,
        children: [
          {
            index: true,
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Home />
              </React.Suspense>
            )
          },
          {
            path: 'explore',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <ExplorePage />
              </React.Suspense>
            )
          },
          {
            path: 'browse',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <BrowsePage />
              </React.Suspense>
            )
          },
          // Pricing & subscription routes TEMPORARILY DISABLED
          // {
          //   path: 'pricing',
          //   element: <React.Suspense fallback={<LoadingScreen />}><PricingPage /></React.Suspense>
          // },
          // {
          //   path: 'subscription/success',
          //   element: <React.Suspense fallback={<LoadingScreen />}><SubscriptionSuccess /></React.Suspense>
          // },
          {
            path: 'analytics',
            element: (
              <AuthGuard>
                <React.Suspense fallback={<LoadingScreen />}>
                  <AnalyticsDashboard />
                </React.Suspense>
              </AuthGuard>
            )
          },
          // Everything Else section routes
          {
            path: 'everything',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <EverythingElseFeed />
              </React.Suspense>
            )
          },
          {
            path: 'everything/create',
            element: (
              <AuthGuard>
                <React.Suspense fallback={<LoadingScreen />}>
                  <CreateExperiencePost />
                </React.Suspense>
              </AuthGuard>
            )
          },
          {
            path: 'everything/post/:id',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <EverythingPostDetail />
              </React.Suspense>
            )
          },
          {
            path: 'everything/post/:id/edit',
            element: (
              <AuthGuard>
                <React.Suspense fallback={<LoadingScreen />}>
                  <CreateExperiencePost />
                </React.Suspense>
              </AuthGuard>
            )
          },
          {
            path: 'everything/:category',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <EverythingElseFeed />
              </React.Suspense>
            )
          },
          {
            path: 'search',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <SearchPage />
              </React.Suspense>
            )
          },
          {
            path: 'create-post',
            element: (
              <AuthGuard>
                <React.Suspense fallback={<LoadingScreen />}>
                  <CreatePost />
                </React.Suspense>
              </AuthGuard>
            )
          },
          {
            path: 'create-service-review',
            element: (
              <AuthGuard>
                <React.Suspense fallback={<LoadingScreen />}>
                  <CreateServiceReview />
                </React.Suspense>
              </AuthGuard>
            )
          },
          {
            path: 'post/:id',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <PostPage />
              </React.Suspense>
            )
          },
          {
            path: 'posts/:id/edit',
            element: (
              <AuthGuard>
                <React.Suspense fallback={<LoadingScreen />}>
                  <PostEditPage />
                </React.Suspense>
              </AuthGuard>
            )
          },
          {
            path: 'notifications',
            element: (
              <AuthGuard>
                <React.Suspense fallback={<LoadingScreen />}>
                  <NotificationsPage />
                </React.Suspense>
              </AuthGuard>
            )
          },
          {
            path: 'messages',
            element: (
              <AuthGuard>
                <React.Suspense fallback={<LoadingScreen />}>
                  <Messages />
                </React.Suspense>
              </AuthGuard>
            )
          },
          {
            path: 'profile',
            element: (
              <RequireAuth>
                <React.Suspense fallback={<LoadingScreen />}>
                  <SessionProfilePage />
                </React.Suspense>
              </RequireAuth>
            )
          },
          {
            path: 'profile/posts',
            element: (
              <RequireAuth key="profile-posts">
                <React.Suspense fallback={<LoadingScreen />}>
                  <SessionProfilePage key="posts" />
                </React.Suspense>
              </RequireAuth>
            )
          },
          {
            path: 'profile/media',
            element: (
              <RequireAuth key="profile-media">
                <React.Suspense fallback={<LoadingScreen />}>
                  <SessionProfilePage key="media" />
                </React.Suspense>
              </RequireAuth>
            )
          },
          {
            path: 'profile/about',
            element: (
              <RequireAuth key="profile-about">
                <React.Suspense fallback={<LoadingScreen />}>
                  <SessionProfilePage key="about" />
                </React.Suspense>
              </RequireAuth>
            )
          },
          {
            path: 'profile/followers',
            element: (
              <RequireAuth key="profile-followers">
                <React.Suspense fallback={<LoadingScreen />}>
                  <SessionProfilePage key="followers" />
                </React.Suspense>
              </RequireAuth>
            )
          },
          {
            path: 'profile/following',
            element: (
              <RequireAuth key="profile-following">
                <React.Suspense fallback={<LoadingScreen />}>
                  <SessionProfilePage key="following" />
                </React.Suspense>
              </RequireAuth>
            )
          },
          {
            path: 'profile/saved',
            element: (
              <RequireAuth key="profile-saved">
                <React.Suspense fallback={<LoadingScreen />}>
                  <SessionProfilePage key="saved" />
                </React.Suspense>
              </RequireAuth>
            )
          },
          {
            path: 'profile/comments',
            element: (
              <RequireAuth key="profile-comments">
                <React.Suspense fallback={<LoadingScreen />}>
                  <SessionProfilePage key="comments" />
                </React.Suspense>
              </RequireAuth>
            )
          },
          {
            path: 'profile/activity',
            element: (
              <RequireAuth key="profile-activity">
                <React.Suspense fallback={<LoadingScreen />}>
                  <SessionProfilePage key="activity" />
                </React.Suspense>
              </RequireAuth>
            )
          },
          {
            path: 'profile/:username',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <LegacyProfileRedirect />
              </React.Suspense>
            )
          },
          {
            path: 'users/:username',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'users/:username/posts',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'users/:username/about',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'users/:username/media',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'users/:username/activity',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'users/:username/likes',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'users/:username/comments',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'users/:username/followers',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'users/:username/following',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'profile/:username/posts',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'profile/:username/about',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'profile/:username/media',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'profile/:username/activity',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'profile/:username/likes',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'profile/:username/comments',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'profile/:username/followers',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'profile/:username/following',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Profile />
              </React.Suspense>
            )
          },
          {
            path: 'categories',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <CategoriesPage />
              </React.Suspense>
            )
          },
          {
            path: 'categories/:categorySlug',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <CategoryPage />
              </React.Suspense>
            )
          },
          {
            path: 'categories/:categorySlug/:subcategorySlug',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <CategoryPage />
              </React.Suspense>
            )
          },
          {
            path: 'categories/analytics',
            element: (
              <AuthGuard>
                <React.Suspense fallback={<LoadingScreen />}>
                  <CategoryAnalyticsPage />
                </React.Suspense>
              </AuthGuard>
            )
          },
          {
            path: 'categories/trending',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <CategoriesPage />
              </React.Suspense>
            )
          },
          {
            path: 'brands',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <BrandsPage />
              </React.Suspense>
            )
          },
          {
            path: 'brands/:brandName',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <BrandPage />
              </React.Suspense>
            )
          },
          {
            path: 'settings',
            element: (
              <AuthGuard>
                <React.Suspense fallback={<LoadingScreen />}>
                  <Settings />
                </React.Suspense>
              </AuthGuard>
            )
          },
          {
            path: 'settings/business',
            element: (
              <AuthGuard>
                <React.Suspense fallback={<LoadingScreen />}>
                  <BusinessSettings />
                </React.Suspense>
              </AuthGuard>
            )
          },
          {
            path: 'business/setup',
            element: (
              <AuthGuard>
                <React.Suspense fallback={<LoadingScreen />}>
                  <BusinessSetupWizard />
                </React.Suspense>
              </AuthGuard>
            )
          },
          // Debug/demo pages — development only (see lazy imports at the top of this file)
          ...(process.env.NODE_ENV !== 'production'
            ? [
                {
                  path: 'debug-auth',
                  element: (
                    <React.Suspense fallback={<LoadingScreen />}>
                      <DebugAuth />
                    </React.Suspense>
                  )
                },
                {
                  path: 'quick-login',
                  element: (
                    <React.Suspense fallback={<LoadingScreen />}>
                      <QuickLogin />
                    </React.Suspense>
                  )
                },
                {
                  path: 'test-no-auth',
                  element: (
                    <React.Suspense fallback={<LoadingScreen />}>
                      <TestNoAuth />
                    </React.Suspense>
                  )
                },
                {
                  path: 'test-responsive',
                  element: (
                    <React.Suspense fallback={<LoadingScreen />}>
                      <ResponsiveTest />
                    </React.Suspense>
                  )
                },
                {
                  path: 'reddit-clone',
                  element: (
                    <React.Suspense fallback={<LoadingScreen />}>
                      <RedditClonePage />
                    </React.Suspense>
                  )
                },
                {
                  path: 'reddit-style-demo',
                  element: (
                    <React.Suspense fallback={<LoadingScreen />}>
                      <RedditStyleDemo />
                    </React.Suspense>
                  )
                }
              ]
            : []),
          {
            path: 'support',
            element: <React.Suspense fallback={<LoadingScreen />}><SupportPage /></React.Suspense>
          },
          // Policy pages
          {
            path: 'policies/privacy',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <PrivacyPolicyPage />
              </React.Suspense>
            )
          },
          {
            path: 'policies/terms',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <TermsOfServicePage />
              </React.Suspense>
            )
          },
          {
            path: 'policies/cookies',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <CookiePolicyPage />
              </React.Suspense>
            )
          },
          {
            path: 'policies/data',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <DataProtectionPage />
              </React.Suspense>
            )
          },
          {
            path: 'policies/service-agreement',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <ServiceAgreementPage />
              </React.Suspense>
            )
          },
          {
            path: 'policies/standards',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <ServiceStandardsPage />
              </React.Suspense>
            )
          },
          {
            path: 'policies/disputes',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <DisputeResolutionPage />
              </React.Suspense>
            )
          },
          {
            path: 'admin',
            element: (
              <AdminGuard>
                <React.Suspense fallback={<LoadingScreen />}>
                  <AdminLayout />
                </React.Suspense>
              </AdminGuard>
            ),
            children: [
              {
                index: true,
                element: (
                  <React.Suspense fallback={<LoadingScreen />}>
                    <AdminDashboard />
                  </React.Suspense>
                )
              },
              {
                path: 'users',
                element: (
                  <React.Suspense fallback={<LoadingScreen />}>
                    <UserManagement />
                  </React.Suspense>
                )
              },
              {
                path: 'verifications',
                element: (
                  <React.Suspense fallback={<LoadingScreen />}>
                    <Verifications />
                  </React.Suspense>
                )
              },
              {
                path: 'moderation/reports',
                element: (
                  <React.Suspense fallback={<LoadingScreen />}>
                    <Reports />
                  </React.Suspense>
                )
              },
              {
                path: 'settings',
                element: (
                  <React.Suspense fallback={<LoadingScreen />}>
                    <AdminSettings />
                  </React.Suspense>
                )
              },
              {
                path: 'metrics',
                element: (
                  <React.Suspense fallback={<LoadingScreen />}>
                    <Metrics />
                  </React.Suspense>
                )
              },
              {
                path: 'security',
                element: (
                  <React.Suspense fallback={<LoadingScreen />}>
                    <Security />
                  </React.Suspense>
                )
              },
              // Ad campaigns TEMPORARILY DISABLED
              // {
              //   path: 'ad-campaigns',
              //   element: <React.Suspense fallback={<LoadingScreen />}><AdCampaigns /></React.Suspense>
              // }
            ]
          }
        ]
          }
        ]
      },
      {
        path: 'auth',
        element: <RouterAwareAuthProvider />,
        children: [
          {
            path: '',
            element: <AuthLayout />,
            children: [
              {
                path: 'login',
                element: (
                  <React.Suspense fallback={<LoadingScreen />}>
                    <Login />
                  </React.Suspense>
                )
              },
              {
                path: 'register',
                element: (
                  <React.Suspense fallback={<LoadingScreen />}>
                    <Register />
                  </React.Suspense>
                )
              }
            ]
          }
        ]
      },
      {
        path: 'login',
        element: <RouterAwareAuthProvider />,
        children: [
          {
            path: '',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Login />
              </React.Suspense>
            )
          }
        ]
      },
      {
        path: 'register',
        element: <RouterAwareAuthProvider />,
        children: [
          {
            path: '',
            element: (
              <React.Suspense fallback={<LoadingScreen />}>
                <Register />
              </React.Suspense>
            )
          }
        ]
      },
      {
        path: 'forgot-password',
        element: <RouterAwareAuthProvider />,
        children: [{
          index: true,
          element: <React.Suspense fallback={<LoadingScreen />}><ForgotPasswordPage /></React.Suspense>
        }]
      },
      {
        path: 'verify-email/:token',
        element: <RouterAwareAuthProvider />,
        children: [{
          index: true,
          element: <React.Suspense fallback={<LoadingScreen />}><VerifyEmailPage /></React.Suspense>
        }]
      },
      {
        path: 'reset-password/:token',
        element: <RouterAwareAuthProvider />,
        children: [{
          index: true,
          element: <React.Suspense fallback={<LoadingScreen />}><ResetPasswordPage /></React.Suspense>
        }]
      },
      {
        path: '*',
        element: (
          <React.Suspense fallback={<LoadingScreen />}>
            <NotFound />
          </React.Suspense>
        )
      }
    ]
  }
];

export const router = createBrowserRouter(routes);

